import Head from "next/head"
import Link from "next/link"
import { BlitzPage, Routes } from "@blitzjs/next"
import { useRouter } from "next/router"
import { useQuery, useMutation } from "@blitzjs/rpc"
import { useState } from "react"
import Layout from "app/core/layouts/Layout"
import Suspense from "app/core/components/Suspense"
import getEmailTemplates from "app/emailTemplates/queries/getEmailTemplates"
import deleteEmailTemplate from "app/emailTemplates/mutations/deleteEmailTemplate"
import Container from "@mui/material/Container"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import TableContainer from "@mui/material/TableContainer"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Stack from "@mui/material/Stack"
import Button from "@mui/material/Button"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import CopyIcon from "@mui/icons-material/ContentCopy"
import Tooltip from "@mui/material/Tooltip"
import { Snackbar, SnackbarState } from "app/core/components/Snackbar"
import CopyDialog from "app/emailTemplates/components/CopyDialog"
import copyEmailTemplate from "app/emailTemplates/mutations/copyEmailTemplate"
import { EmailsTab } from "app/emailTemplates/components/emailsTabs"
import { Margin } from "@mui/icons-material"

const EmailTemplateRow = ({
  emailTemplate,
  deleteEmailTemplateMutation,
  copyEmailTemplateMutation,
  copyEmailTemplateMutationIsLoading,
  refetch,
  setSnackbarData,
}) => {
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)

  return (
    <TableRow key={emailTemplate.id} hover={true}>
      <TableCell>{emailTemplate.id}</TableCell>
      <TableCell>{emailTemplate.brand.name}</TableCell>
      <TableCell>{emailTemplate.title}</TableCell>
      <TableCell>{emailTemplate.from}</TableCell>
      <TableCell>{emailTemplate.replyTo}</TableCell>
      <TableCell>{emailTemplate.isChance ? "Yes" : "No"}</TableCell>
      <TableCell>
        {emailTemplate.emailTemplateTranslations.map((t) => t.language.name).join(", ")}
      </TableCell>
      <TableCell sx={{ py: 0 }}>
        <Stack direction="row" justifyContent="flex-end">
          <Link
            href={Routes.EditEmailTemplatePage({ emailTemplateId: emailTemplate.id })}
            legacyBehavior
          >
            <IconButton>
              <EditIcon />
            </IconButton>
          </Link>
          <IconButton
            onClick={async () => {
              if (window.confirm("This will be deleted")) {
                await deleteEmailTemplateMutation({ id: emailTemplate.id })
                await refetch()
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
          <Tooltip title="Copy email template">
            <IconButton
              disabled={copyEmailTemplateMutationIsLoading}
              onClick={() => setCopyDialogOpen(true)}
            >
              <CopyIcon />
            </IconButton>
          </Tooltip>
          <CopyDialog
            emailTemplate={emailTemplate}
            open={copyDialogOpen}
            setOpen={setCopyDialogOpen}
            copyEmailTemplateMutation={copyEmailTemplateMutation}
            setSnackbarData={setSnackbarData}
          />
        </Stack>
      </TableCell>
    </TableRow>
  )
}

export const EmailTemplatesList = () => {
  const router = useRouter()
  const page = Number(router.query.page) || 0
  const [emailTemplates, { refetch }] = useQuery(getEmailTemplates, {
    orderBy: { id: "asc" },
  })
  const [deleteEmailTemplateMutation] = useMutation(deleteEmailTemplate)
  const [snackbarData, setSnackbarData] = useState<SnackbarState>({
    type: "success",
    text: "",
    show: false,
  })
  const [copyEmailTemplateMutation, { isLoading: copyEmailTemplateMutationIsLoading }] =
    useMutation(copyEmailTemplate)

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Brand</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>From</TableCell>
            <TableCell>Reply-To</TableCell>
            <TableCell>Set chance price by default</TableCell>
            <TableCell>Languages</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {emailTemplates.map((emailTemplate) => (
            <EmailTemplateRow
              key={`row-${emailTemplate.id}`}
              emailTemplate={emailTemplate}
              copyEmailTemplateMutation={copyEmailTemplateMutation}
              copyEmailTemplateMutationIsLoading={copyEmailTemplateMutationIsLoading}
              deleteEmailTemplateMutation={deleteEmailTemplateMutation}
              refetch={refetch}
              setSnackbarData={setSnackbarData}
            />
          ))}
        </TableBody>
      </Table>
      <Snackbar snackbarData={snackbarData} setSnackbarData={setSnackbarData} />
    </TableContainer>
  )
}

const EmailTemplatesPage: BlitzPage = () => {
  return (
    <>
      <Head>
        <title>EmailTemplates</title>
      </Head>

      <Container maxWidth={false}>
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Typography sx={{ my: 4, flexGrow: 1 }} variant="h4" component="h2">
            Email Templates
          </Typography>

          <Box>
            <Link href={Routes.NewEmailTemplatePage()} legacyBehavior>
              <Button variant="contained" startIcon={<AddIcon />}>
                Create
              </Button>
            </Link>
          </Box>
        </Box>
        <div style={{ marginBottom: 10 }}>
          <EmailsTab defaultTabState="emails-templates" route="/emails/emailDomainCorrections" />
        </div>
        <Suspense>
          <EmailTemplatesList />
        </Suspense>
      </Container>
    </>
  )
}

EmailTemplatesPage.authenticate = true
EmailTemplatesPage.getLayout = (page) => <Layout>{page}</Layout>

export default EmailTemplatesPage
